import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Scale, Handshake, AlertTriangle, Ban, Gavel, RefreshCw, Mail, Shield } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useEffect } from "react";

const TermsOfService = () => {
  const { language } = useLanguage();
  const isArabic = language === "ar";

  useEffect(() => {
    document.title = isArabic
      ? "شروط الخدمة - مؤسسة العربية الواحة"
      : "Terms of Service - Arabian Al Waha Est";
    window.scrollTo(0, 0);
  }, [isArabic]);

  const t = {
    back: isArabic ? "العودة إلى الموقع" : "Back to website",
    title: isArabic ? "شروط الخدمة" : "Terms of Service",
    subtitle: isArabic ? "آخر تحديث: 7 يونيو 2026" : "Last updated: June 7, 2026",
    intro: isArabic
      ? "تحكم هذه الشروط استخدامك لموقع مؤسسة العربية الواحة وخدماتها. باستخدامك للموقع أو طلب أي من خدماتنا، فإنك توافق على الالتزام بهذه الشروط."
      : "These terms govern your use of the Arabian Al Waha Est website and services. By using our website or requesting any of our services, you agree to be bound by these terms.",
    sections: [
      {
        icon: Handshake,
        title: isArabic ? "قبول الشروط" : "Acceptance of Terms",
        content: isArabic
          ? "بدخولك إلى هذا الموقع أو استخدامه، فإنك تقر بأنك قرأت هذه الشروط وفهمتها ووافقت عليها. إذا كنت لا توافق، يُرجى عدم استخدام الموقع أو خدماتنا."
          : "By accessing or using this website, you acknowledge that you have read, understood, and agree to these terms. If you do not agree, please refrain from using the site or our services.",
      },
      {
        icon: FileText,
        title: isArabic ? "الخدمات المقدمة" : "Services Provided",
        content: isArabic
          ? "نقدم خدمات المقاولات الصناعية والتجارة وتأجير المعدات والمركبات وتوفير القوى العاملة. تخضع جميع الخدمات لعقود منفصلة وعروض أسعار رسمية تحدد النطاق والسعر والتسليم."
          : "We provide industrial contracting, trading, equipment and vehicle rental, and workforce supply services. All services are subject to separate contracts and formal quotations that define scope, pricing, and delivery.",
      },
      {
        icon: Scale,
        title: isArabic ? "عروض الأسعار والعقود" : "Quotations & Contracts",
        content: isArabic
          ? "عروض الأسعار صالحة للمدة المذكورة فيها (30 يوماً افتراضياً) وقابلة للمراجعة بناءً على نطاق العمل النهائي. يصبح أي ارتباط ملزماً فقط بعد توقيع عقد رسمي من الطرفين."
          : "Quotations are valid for the period stated within them (30 days by default) and are subject to revision based on the final scope of work. Any engagement becomes binding only upon a formal contract signed by both parties.",
      },
      {
        icon: Shield,
        title: isArabic ? "الملكية الفكرية" : "Intellectual Property",
        content: isArabic
          ? "جميع المحتويات والشعارات والصور والنصوص على هذا الموقع هي ملكية حصرية لمؤسسة العربية الواحة. يُمنع النسخ أو إعادة الإنتاج أو التوزيع دون إذن خطي مسبق."
          : "All content, logos, images, and text on this website are the exclusive property of Arabian Al Waha Est. Copying, reproduction, or distribution without prior written permission is strictly prohibited.",
      },
      {
        icon: Ban,
        title: isArabic ? "الاستخدام المحظور" : "Prohibited Use",
        content: isArabic
          ? "لا يجوز استخدام الموقع لأي غرض غير قانوني، أو لإرسال محتوى ضار، أو لمحاولة الوصول غير المصرح به إلى أنظمتنا، أو لانتحال شخصية أي جهة أو فرد."
          : "You may not use the site for any unlawful purpose, to transmit harmful content, to attempt unauthorized access to our systems, or to impersonate any entity or individual.",
      },
      {
        icon: AlertTriangle,
        title: isArabic ? "إخلاء المسؤولية" : "Disclaimer of Warranties",
        content: isArabic
          ? "يُقدَّم محتوى الموقع \"كما هو\" دون أي ضمانات. نبذل قصارى جهدنا لضمان دقة المعلومات، إلا أننا لا نضمن خلوها تماماً من الأخطاء أو التحديثات."
          : "Website content is provided \"as is\" without warranties of any kind. While we make every effort to ensure accuracy, we do not guarantee that information is free from errors or always up to date.",
      },
      {
        icon: Gavel,
        title: isArabic ? "تحديد المسؤولية" : "Limitation of Liability",
        content: isArabic
          ? "لا تتحمل المؤسسة المسؤولية عن أي أضرار غير مباشرة أو عرضية أو تبعية تنشأ عن استخدامك للموقع، إلا في الحدود التي يقتضيها النظام المعمول به في المملكة العربية السعودية."
          : "The company is not liable for any indirect, incidental, or consequential damages arising from your use of the website, except as required by the applicable laws of the Kingdom of Saudi Arabia.",
      },
      {
        icon: Scale,
        title: isArabic ? "القانون الحاكم" : "Governing Law",
        content: isArabic
          ? "تخضع هذه الشروط لأنظمة المملكة العربية السعودية. أي نزاع ينشأ عن استخدام الموقع أو الخدمات يخضع للاختصاص الحصري للمحاكم المختصة في المملكة."
          : "These terms are governed by the laws of the Kingdom of Saudi Arabia. Any dispute arising from the use of the website or services is subject to the exclusive jurisdiction of the competent courts in the Kingdom.",
      },
      {
        icon: RefreshCw,
        title: isArabic ? "تحديث الشروط" : "Updates to Terms",
        content: isArabic
          ? "نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم نشر التحديثات على هذه الصفحة مع تاريخ المراجعة. استمرارك في استخدام الموقع يُعد قبولاً للنسخة المحدّثة."
          : "We reserve the right to modify these terms at any time. Updates will be posted on this page with a revision date. Continued use of the website constitutes acceptance of the updated version.",
      },
      {
        icon: Mail,
        title: isArabic ? "اتصل بنا" : "Contact Us",
        content: isArabic
          ? "لأي استفسارات حول هذه الشروط، يُرجى التواصل عبر: sales@arabianalwaha.com"
          : "For any questions about these terms, please contact us at: sales@arabianalwaha.com",
      },
    ],
    footerNote: isArabic
      ? "باستخدامك لخدماتنا، فإنك توافق على شروط الخدمة هذه."
      : "By using our services, you agree to these terms of service.",
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isArabic ? "rtl" : "ltr"}>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="container mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-primary/80">
        <div className="container mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <Scale className="w-12 h-12 text-secondary mx-auto mb-6" />
            <h1 className="text-white font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
              {t.title}
            </h1>
            <p className="text-white/70 text-sm">{t.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-6 lg:px-10 max-w-3xl">
          <p className="text-lg text-muted-foreground leading-relaxed text-center">{t.intro}</p>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <div key={index} className="bg-card rounded-xl p-6 lg:p-8 shadow-soft border border-border/40 hover:shadow-elegant transition-elegant">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center shrink-0">
                    <section.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-accent mb-3">{section.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">{t.footerNote}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;